/**
 * Compact board codes.
 *
 * A code captures WHICH LEVEL and WHERE EVERY PLAYER TILE SITS, so a board can
 * be pasted into chat, a bug report, or another device.
 *
 * Why it is this small: there are exactly 15 functionally distinct player tile
 * states (straight x2 rotations, corner x4, cross x1, mixer x4, splitter x4)
 * plus "empty" = 16, which is precisely one nibble per cell. Rotation classes
 * collapse first (a straight at 180 plays identically to 0, a cross is the
 * same at all four), so nothing is spent encoding differences the game cannot
 * observe. Nibbles are packed into a bitstream and read out 6 bits at a time
 * as base64url, and trailing empty cells are dropped because the grid size is
 * already known from the level.
 *
 * Result: a 6x6 board is ~24 characters, a 4x4 about 11.
 */
import { ROTATION_CLASSES, type Rotation, type TileId } from './sim/tiles.ts';

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const B36 = '0123456789abcdefghijklmnopqrstuvwxyz';

/** The 16 cell states, index = nibble value. Order is part of the format. */
export const CELL_STATES: ({ tile: TileId; rotation: Rotation } | null)[] = [
  null, // 0 empty
  { tile: 'pipe_straight', rotation: 0 },
  { tile: 'pipe_straight', rotation: 90 },
  { tile: 'pipe_corner', rotation: 0 },
  { tile: 'pipe_corner', rotation: 90 },
  { tile: 'pipe_corner', rotation: 180 },
  { tile: 'pipe_corner', rotation: 270 },
  { tile: 'pipe_cross', rotation: 0 },
  { tile: 'mixer', rotation: 0 },
  { tile: 'mixer', rotation: 90 },
  { tile: 'mixer', rotation: 180 },
  { tile: 'mixer', rotation: 270 },
  { tile: 'splitter', rotation: 0 },
  { tile: 'splitter', rotation: 90 },
  { tile: 'splitter', rotation: 180 },
  { tile: 'splitter', rotation: 270 },
];

/**
 * Collapse a rotation to its canonical class member, so two boards that play
 * identically always produce the same code.
 */
export function canonicalRotation(tile: TileId, rotation: Rotation): Rotation {
  const classes = ROTATION_CLASSES[tile];
  if (classes.includes(rotation)) return rotation;
  // straight: 180 -> 0, 270 -> 90 ; cross: everything -> 0
  const reduced = ((rotation % 180) as Rotation);
  return classes.includes(reduced) ? reduced : (classes[0] ?? 0);
}

export function tileToNibble(tile: TileId, rotation: Rotation): number {
  const rot = canonicalRotation(tile, rotation);
  const i = CELL_STATES.findIndex((s) => s && s.tile === tile && s.rotation === rot);
  return i < 0 ? 0 : i;
}

export function nibbleToTile(n: number): { tile: TileId; rotation: Rotation } | null {
  return CELL_STATES[n] ?? null;
}

/** Pack nibbles (0..15) into base64url, dropping trailing empties. */
export function packNibbles(cells: number[]): string {
  const trimmed = [...cells];
  while (trimmed.length > 0 && trimmed[trimmed.length - 1] === 0) trimmed.pop();
  if (trimmed.length === 0) return '';

  let out = '';
  let acc = 0;
  let bits = 0;
  for (const n of trimmed) {
    acc = (acc << 4) | (n & 15);
    bits += 4;
    while (bits >= 6) {
      bits -= 6;
      out += B64[(acc >> bits) & 63];
    }
  }
  if (bits > 0) out += B64[(acc << (6 - bits)) & 63];
  return out;
}

/** Inverse of packNibbles, padded out to `total` cells. */
export function unpackNibbles(s: string, total: number): number[] {
  const out: number[] = [];
  let acc = 0;
  let bits = 0;
  for (const ch of s) {
    const v = B64.indexOf(ch);
    if (v < 0) throw new Error(`bad character '${ch}' in code`);
    acc = (acc << 6) | v;
    bits += 6;
    while (bits >= 4 && out.length < total) {
      bits -= 4;
      out.push((acc >> bits) & 15);
    }
  }
  while (out.length < total) out.push(0);
  return out.slice(0, total);
}

function checksum(s: string): string {
  let n = 0;
  for (let i = 0; i < s.length; i++) n = (n * 31 + s.charCodeAt(i)) % 36;
  return B36[n]!;
}

export interface BoardCode {
  levelIndex: number;
  cells: number[];
}

/**
 * `<level base36><payload base64url><checksum base36>`
 *
 * The checksum is one character and catches the common paste failure - a code
 * truncated by a line wrap - which would otherwise load a silently different
 * board.
 */
export function encodeBoard(levelIndex: number, cells: number[]): string {
  const head = B36[levelIndex % 36] ?? '0';
  const body = packNibbles(cells);
  return head + body + checksum(head + body);
}

export function decodeBoard(code: string, total: number): BoardCode {
  const clean = code.trim().replace(/\s+/g, '');
  if (clean.length < 2) throw new Error('code is too short');
  const head = clean.slice(0, 1);
  const body = clean.slice(1, -1);
  const check = clean.slice(-1);
  if (checksum(head + body) !== check) {
    throw new Error('checksum mismatch - the code looks truncated or edited');
  }
  const levelIndex = B36.indexOf(head);
  if (levelIndex < 0) throw new Error('bad level character');
  return { levelIndex, cells: unpackNibbles(body, total) };
}

/** Read just the level index, before the grid size is known. */
export function peekLevelIndex(code: string): number {
  const i = B36.indexOf(code.trim().slice(0, 1));
  if (i < 0) throw new Error('bad level character');
  return i;
}
